import { AnyAction } from '@reduxjs/toolkit';
import { combineEpics, StateObservable } from 'redux-observable';
import { catchError, filter, map, mergeMap, Observable, of, switchMap } from 'rxjs';
import { WidgetDisplayMode } from '../../../../Models/Widget/WidgetDisplayMode';
import { DEFAULT_GAME_LANGUAGE } from '../../../../Utils/Constants';
import { mapPayload } from '../../../../Utils/RxjsOperators/MapPayload';
import WidgetFrontActions from '../../Pages/WidgetFront/WidgetFrontActions';
import { StoreDependencies } from '../StoreDependencies';
import { StoreState } from '../StoreState';
import UserStoreActions from './UserStoreActions';

function updateCurrentUserSettings(
  $action: Observable<AnyAction>,
  $state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(UserStoreActions.updateCurrentUserSettings.match),
    filter(() => !!$state.value.user.currentUser?.settings),
    mapPayload(),
    map((settings) => ({
      settings,
      currentSettings: $state.value.user.currentUser!.settings
    })),
    map(({ settings, currentSettings }) => ({ ...currentSettings, ...settings })),
    mergeMap((newSettings) => dependencies.userManager!.updateSettings(newSettings)),
    mergeMap((settings) => [
      UserStoreActions.setCurrentUserSettings(settings),
      WidgetFrontActions.updateSettings(settings.widget)
    ])
  );
}

function updateCurrentUserWidgetSettings(
  $action: Observable<AnyAction>,
  $state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(UserStoreActions.updateCurrentUserWidgetSettings.match),
    filter(() => !!$state.value.user.currentUser?.settings),
    mapPayload(),
    map((widgetSettings) => ({
      widgetSettings,
      currentSettings: $state.value.user.currentUser!.settings
    })),
    map(({ widgetSettings, currentSettings }) => ({
      ...currentSettings,
      widget: { ...currentSettings.widget, ...widgetSettings }
    })),
    mergeMap((newSettings) => dependencies.userManager!.updateSettings(newSettings)),
    mergeMap((settings) => [
      UserStoreActions.setCurrentUserSettings(settings),
      WidgetFrontActions.updateSettings(settings.widget)
    ])
  );
}

function resetCurrentUserWidgetSettings(
  $action: Observable<AnyAction>,
  $state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(UserStoreActions.resetCurrentUserWidgetSettings.match),
    filter(() => !!$state.value.user.currentUser?.settings),
    map(() => $state.value.user.currentUser!.settings),
    map((currentSettings) => ({
      ...currentSettings,
      widget: {
        displayMode: WidgetDisplayMode.all,
        moonLanguage: DEFAULT_GAME_LANGUAGE,
        scale: 75
      }
    })),
    mergeMap((newSettings) => dependencies.userManager!.updateSettings(newSettings)),
    mergeMap((settings) => [
      UserStoreActions.setCurrentUserSettings(settings),
      WidgetFrontActions.updateSettings(settings.widget)
    ])
  );
}

function getCurrentUserMoonLists(
  $action: Observable<AnyAction>,
  _$state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(UserStoreActions.getCurrentUserMoonLists.match),
    switchMap(() => dependencies.moonListManager!.getCurrentUserMoonList()),
    map(UserStoreActions.setCurrentUserMoonLists),
    catchError(() => of(UserStoreActions.setCurrentUserMoonLists([])))
  );
}

function favoriteMoonList(
  $action: Observable<AnyAction>,
  _$state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(UserStoreActions.favoriteMoonListWithId.match),
    mapPayload(),
    switchMap((moonListId) => dependencies.moonListManager!.favoriteMoonListWithId(moonListId)),
    map(({ action, moonList }) =>
      action === 'added'
        ? UserStoreActions.addFavoriteMoonList(moonList)
        : UserStoreActions.removeFavoriteMoonListWithId(moonList.id)
    )
  );
}

export default combineEpics(
  updateCurrentUserSettings,
  updateCurrentUserWidgetSettings,
  resetCurrentUserWidgetSettings,
  getCurrentUserMoonLists,
  favoriteMoonList
);
